import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Clock } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { TimeRangeInput, type TimeRangeInputValue, computesCrossesMidnight } from '@/components/TimeRangeInput';
import { DOW_LABELS } from '@/lib/calendar-day';

/**
 * `/settings/default-work-patterns` (v0.4 追加要件⑩ D-328 再設計、D-333〜D-339)。
 * 設計: docs/design/v04-hq-staff-and-support-scope.md §8-4
 *
 * 「調整不要な既定の勤務パターン」マスタ。shift_patterns (`/settings/shift-patterns`) と異なり
 * ノード別上書きを持たない (D-334)。曜日ごとに「勤務なし (行が無い)」か「開始〜終了時刻」かを
 * 設定する (§6-0 原則3、週休の日数を分岐で持たない)。
 */

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';

type PatternType = { pattern_type_id: string; code: string; label: string; sort_order: number };
type Entry = { day_of_week: number; start_time: string; end_time: string; crosses_midnight: boolean; break_minutes: number };

function DayRow({
  day, entry, patternTypeId, canManage,
}: { day: number; entry: Entry | undefined; patternTypeId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<TimeRangeInputValue>({
    startTime: entry?.start_time ?? '09:00', endTime: entry?.end_time ?? '17:00',
  });
  const [breakMinutes, setBreakMinutes] = useState(String(entry?.break_minutes ?? 0));

  const invalidate = () => qc.invalidateQueries({ queryKey: ['default-work-pattern-entries', patternTypeId] });
  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/settings/default-work-pattern-types/${patternTypeId}/entries`, {
      method: 'PUT',
      body: JSON.stringify({
        day_of_week: day, start_time: value.startTime, end_time: value.endTime,
        crosses_midnight: computesCrossesMidnight(value), break_minutes: Number(breakMinutes) || 0,
      }),
    }),
    onSuccess: () => { invalidate(); toast.success('保存しました'); setEditing(false); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const clearMut = useMutation({
    mutationFn: () => apiFetch(`/settings/default-work-pattern-types/${patternTypeId}/entries?day_of_week=${day}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(); toast.success('「勤務なし」に戻しました'); setEditing(false); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  const canSave = !!value.startTime && !!value.endTime;

  return (
    <li className="flex items-center justify-between gap-2 border border-surface-line rounded p-2 text-sm">
      <span className="w-10 shrink-0 font-semibold text-text-body">{DOW_LABELS[day]}</span>
      {entry ? (
        <span className="flex-1 text-text-body">{entry.start_time}〜{entry.end_time}{entry.break_minutes > 0 && ` (休憩${entry.break_minutes}分)`}</span>
      ) : (
        <span className="flex-1 text-text-sub">勤務なし</span>
      )}
      {canManage && (
        <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setEditing(true)}>
          {entry ? '編集' : '設定する'}
        </Button>
      )}

      <Modal open={editing} title={`${DOW_LABELS[day]}曜日の時刻`} onClose={() => setEditing(false)} size="sm"
        footer={(
          <>
            {entry && <Button variant="ghost" onClick={() => clearMut.mutate()} loading={clearMut.isPending}>「勤務なし」に戻す</Button>}
            <Button variant="secondary" onClick={() => setEditing(false)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
          </>
        )}>
        <div className="space-y-4">
          <TimeRangeInput value={value} onChange={setValue} />
          <div>
            <label className="block text-sm text-text-sub mb-1">休憩時間 (分)</label>
            <input type="number" min={0} value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} className={inputCls} />
          </div>
        </div>
      </Modal>
    </li>
  );
}

/** ⚠️ Modal (曜日別時刻編集) をこのパネル自体では開かない — DayRow が自身の Modal を開くため、
 *  このパネルを Modal にすると「Modal の中で Modal を開く」構造になり Escape キーの二重発火
 *  (design-expert HIGH指摘) を招く。ShiftPatternsSettingsPage.tsx の PatternTimeEditor と同じ
 *  「パターン一覧の <li> 直下にインラインで展開する」単一階層の構成に揃える。 */
function EntriesPanel({ patternTypeId, canManage }: { patternTypeId: string; canManage: boolean }) {
  const { data, isLoading } = useQuery<{ entries: Entry[] }>({
    queryKey: ['default-work-pattern-entries', patternTypeId],
    queryFn: () => apiFetch(`/settings/default-work-pattern-types/${patternTypeId}/entries`),
  });
  const entriesByDay = new Map((data?.entries ?? []).map((e) => [e.day_of_week, e] as const));

  return (
    <div className="mt-2 pt-2 border-t border-surface-line">
      {isLoading ? (
        <div className="text-text-sub text-sm">読み込み中…</div>
      ) : (
        <ul className="space-y-2">
          {DOW_LABELS.map((_, day) => (
            <DayRow key={day} day={day} entry={entriesByDay.get(day)} patternTypeId={patternTypeId} canManage={canManage} />
          ))}
        </ul>
      )}
    </div>
  );
}

export function DefaultWorkPatternsSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'tenant_config', 'manage');
  const qc = useQueryClient();
  const [draft, setDraft] = useState<{ pattern_type_id: string | null; code: string; label: string; sort_order: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PatternType | null>(null);
  const [entriesFor, setEntriesFor] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ pattern_types: PatternType[] }>({
    queryKey: ['default-work-pattern-types'],
    queryFn: () => apiFetch('/settings/default-work-pattern-types'),
  });
  const patternTypes = data?.pattern_types ?? [];

  const saveMut = useMutation({
    mutationFn: (d: NonNullable<typeof draft>) => {
      const payload = { code: d.code.trim(), label: d.label.trim(), sort_order: Number(d.sort_order) || 0 };
      return d.pattern_type_id
        ? apiFetch(`/settings/default-work-pattern-types/${d.pattern_type_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/settings/default-work-pattern-types', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['default-work-pattern-types'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/settings/default-work-pattern-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['default-work-pattern-types'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const canSave = !!draft && draft.code.trim() !== '' && draft.label.trim() !== '';

  return (
    <div className="space-y-3 max-w-3xl">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-semibold text-text-body">既定勤務パターン</h2>
          <p className="text-sm text-text-sub mt-1">
            店舗のシフト編成を必要としない社員 (本社勤務者等) の、曜日ごとの標準的な勤務時刻を登録します。
            社員への割当は「社員詳細」画面で行います。
          </p>
        </div>
        {canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />}
            onClick={() => setDraft({ pattern_type_id: null, code: '', label: '', sort_order: String(patternTypes.length) })}>
            パターンを追加
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : patternTypes.length === 0 ? (
        <EmptyState message="既定勤務パターンがまだありません" description={canManage ? '「パターンを追加」から登録してください。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {patternTypes.map((p) => (
            <li key={p.pattern_type_id} className="border border-surface-border rounded-lg bg-white p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex items-center gap-2">
                  <span className="text-sm font-semibold text-text-body break-words">{p.label}</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{p.code}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" leftIcon={<Clock size={14} />}
                    aria-expanded={entriesFor === p.pattern_type_id}
                    onClick={() => setEntriesFor(entriesFor === p.pattern_type_id ? null : p.pattern_type_id)}>
                    {canManage ? '曜日別時刻を設定' : '曜日別時刻を確認'}
                  </Button>
                  {canManage && (
                    <>
                      <button type="button" aria-label="編集" onClick={() => setDraft({ pattern_type_id: p.pattern_type_id, code: p.code, label: p.label, sort_order: String(p.sort_order) })}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                      <button type="button" aria-label="削除" onClick={() => setDeleteTarget(p)}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              </div>
              {entriesFor === p.pattern_type_id && (
                <EntriesPanel patternTypeId={p.pattern_type_id} canManage={canManage} />
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.pattern_type_id ? 'パターンを編集' : 'パターンを追加'} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50} className={inputCls} placeholder="例: hq_standard" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} placeholder="例: 総合職標準" />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="パターンを削除" message={`「${deleteTarget?.label}」を削除します。このパターンに紐づく曜日別時刻も同時に削除されます。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.pattern_type_id)} />
    </div>
  );
}
